import * as KatalMetrics from '@amzn/katal-metrics';
import {
  KatalMonitoringDriver,
  KatalMonitoringDriverOptions,
} from '@amzn/katal-monitoring-aws-driver';
import {
  REGULATOR_CENTRAL_CLIENT_ID,
  REGULATOR_CENTRAL_SITE,
} from './constants/GlobalConstants';
import { logger } from './logger';

const metricsConsoleErrorHandler = (err: Error) => console.error(err);

const monitoringConfig: KatalMonitoringDriverOptions = {
  url: 'https://shwjzjhc3a.execute-api.us-east-1.amazonaws.com/prod/v1/monitoring',
};

const makeMetricsDriver = () => new KatalMonitoringDriver(monitoringConfig);

const makePublisher = (): KatalMetrics.Publisher => {
  const metricsDriver = makeMetricsDriver();
  const initialMetricsContext = new KatalMetrics.Context.Builder()
    .withSite(REGULATOR_CENTRAL_SITE)
    .withServiceName(REGULATOR_CENTRAL_CLIENT_ID)
    .build();
  return new KatalMetrics.Publisher(
    metricsDriver,
    metricsConsoleErrorHandler,
    initialMetricsContext,
  );
};

export enum MetricsName {
  BACKEND_REQUEST = 'BACKEND_REQUEST',
  LIST_CASE_PAGE = 'LIST_CASE_PAGE',
  CREATE_CASE_PAGE = 'CREATE_CASE_PAGE',
  CORRESPONDENCE_TAB = 'CORRESPONDENCE_TAB',
  AUTH_CONTEXT = 'AUTH_CONTEXT',
  DOWNLOAD_ATTACHMENTS = 'DOWNLOAD_ATTACHMENTS',
  LOCAL_STORAGE = 'LOCAL_STORAGE',
  WAF_CAPTCHA = 'WAF_CAPTCHA',
}

export const metricsPublisher = makePublisher();

export const emitClickMetrics = (
  methodName: string,
  action: string,
  count: number = 1,
) => {
  let actionMetricsPublisher =
    metricsPublisher.newChildActionPublisherForMethod(methodName);
  actionMetricsPublisher.publishCounterMonitor(action, count);
};

export const emitTimerMetrics = (
  methodName: string,
  action: string,
  timeTaken: number,
) => {
  let actionMetricsPublisher =
    metricsPublisher.newChildActionPublisherForMethod(methodName);
  actionMetricsPublisher.publishTimerMonitor(action, timeTaken);
};

/**
 * Hanles error metrics emission by figuring out the status code.
 * response is expected in below format
 * 1. HTTP Response
 * 2. Response from RTK Query{
 *       error : {data : BACKEND_RESPONSE, status: 500}
 *       isUnhandledError: boolean
 *        meta : {request: Request, response: Response}
 *    }
 * The Status Code can be FETCH_ERROR for responses which are not fulfilled.
 * @param response Response | any as described in the doc
 * @param metricsPublisher Publisher for KatalMetrics
 * @param count defaults to 1
 */
export const emitApiErrorMetrics = (
  response: Response | any,
  metricsPublisher: KatalMetrics.Publisher,
  count: number = 1,
) => {
  let statusCode: String | Number | undefined = undefined;
  try {
    statusCode = response?.status || response?.error?.status;
  } catch (error) {
    logger.error(
      `Unable to parse the response object to extract statusCode, Error: ${JSON.stringify(
        response,
      )}`,
    );
  }
  if (!statusCode) {
    logger.error(
      `Invalid statusCode for response: ${JSON.stringify(response)}`,
    );
    emitClickMetrics(MetricsName.BACKEND_REQUEST, 'CAPTURE_METRICS_FAILURE');
  } else {
    metricsPublisher.publishCounterMonitor(`RESPONSE_${statusCode}`, count);
  }
};
